import {useIntl, FormattedNumber} from 'react-intl'
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'

import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Badge,
    Popover,
    PopoverHeader,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverFooter,
    PopoverArrow,
    Text,
    Link,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerType,
    useProducts
} from '@salesforce/commerce-sdk-react'

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

// import Search from '@salesforce/retail-react-app/app/components/search'
import Search from '../../components/search'
//import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
//import {ChevronDownIcon, SignoutIcon} from '@salesforce/retail-react-app/app/components/icons'

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

import {StoreLocatorIcon} from '../icons'

//import {navLinks, messages} from '@salesforce/retail-react-app/app/pages/account/constant'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {HideSeachOnMobile, HideSeachOnDesktop} from '../responsive'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {STORE_LOCATOR_IS_ENABLED} from '@salesforce/retail-react-app/app/constants'
import {
    headerLogoStyle,
    menuIconStyle,
    toggleMenuIconStyle,
    cartHeadingStyle,
    cartBodyStyle,
    cartFooterStyles,
    cartBadgeStyle,
    priceContentStyle,
    priceDiscountContentStyle,
    giftName
} from './index.style'
import {pxToRem} from '../../utills/pxToRem'
import sizes from '../../theme/foundations/sizes'
import {transformBasketData, transformGiftCertificateItems} from '../../utills/transformBasket'
import useSitePreference from '../../hooks/use-site-preference'
import {Link as RouteLink} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
/**
 * Search bar for the header.
 *
 * The search bar is a simple input field with a search icon.
 * It can be used to search for products or navigate to a
 * specific page.
 *
 * @param props {object} the component props
 * @returns {Element} the search bar element
 */
const SearchBar = (props) => {
    const styles = useMultiStyleConfig('Header')
    const intl = useIntl()
    const placeholder = intl.formatMessage({
        id: 'header.field.placeholder.search_for_products',
        defaultMessage: 'Search...'
    })
    return (
        <Box {...styles.searchContainer}>
            <Search aria-label={placeholder} placeholder={placeholder} {...props} />
        </Box>
    )
}

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
    children,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    onWishlistClick = noop,
    onStoreLocatorClick = noop,
    menuToggle,
    setMenuToggle,
    ...props
}) => {
    const {app: appConfig} = getConfig()
    const intl = useIntl()
    const popoverTriggerRef = useRef(null)
    const menuRef = useRef()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()

    const productItemsQuantity =
        basket?.productItems?.reduce((sum, item) => sum + item.quantity, 0) || 0
    const giftCertificateItemsCount = basket?.giftCertificateItems?.length || 0
    const totalItemsWithGiftCards = productItemsQuantity + giftCertificateItemsCount

    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const [miniCartItems, setMiniCartItems] = useState({})
    const {isRegistered} = useCustomerType()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const navigate = useNavigation()
    const {
        getButtonProps: getAccountMenuButtonProps,
        getDisclosureProps: getAccountMenuDisclosureProps,
        isOpen: isAccountMenuOpen,
        onClose: onAccountMenuClose,
        onOpen: onAccountMenuOpen
    } = useDisclosure()

    const [showLoading, setShowLoading] = useState(false)
    const [isAboveHeaderVisible, setIsAboveHeaderVisible] = useState(true)
    // tracking if users enter the popover Content,
    // so we can decide whether to close the menu when users leave account icons
    const hasEnterPopoverContent = useRef()
    const isMobile = useBreakpointValue({base: true, md: false})

    const styles = useMultiStyleConfig('Header')

    const {data} = useSitePreference('pdpVariationCategories')
    const pdpVariationCategory = data?.pdpVariationCategories

    useEffect(() => {
        if (basket && Object.keys(basket).length > 0) {
            let basketItems = []
            let productSubTotal = 0
            // Handle regular product items
            if (products && Object.keys(products).length > 0 && basket.productItems) {
                const transformedProductData = transformBasketData(products, basket)
                basketItems = [...basketItems, ...transformedProductData]
                productSubTotal += parseFloat(basket.productSubTotal || 0)
            }
            // Handle gift certificate items
            if (basket.giftCertificateItems && basket.giftCertificateItems.length > 0) {
                const transformedGiftCertificates = transformGiftCertificateItems(
                    basket.giftCertificateItems
                )
                basketItems = [...basketItems, ...transformedGiftCertificates]
                // Add gift certificate amounts to subtotal
                const giftCertificateTotal = basket.giftCertificateItems.reduce(
                    (total, item) => total + parseFloat(item.amount || 0),
                    0
                )
                productSubTotal += giftCertificateTotal
            }
            const cartItems = {
                basketItems: basketItems,
                productSubTotal: productSubTotal.toFixed(2)
            }
            setMiniCartItems(cartItems)
        }
    }, [products, basket])

    useEffect(() => {
        const handleScroll = () => {
            const aboveHeaderHeight =
                document.querySelector('.above-header-wrapper')?.offsetHeight || 0
            setIsAboveHeaderVisible(window.scrollY < aboveHeaderHeight)
        }

        window.addEventListener('scroll', handleScroll)

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    useEffect(() => {
        if (!appConfig.digioh.enabled) return
        const userAgent = navigator.userAgent

        // skip specific bots or tools
        const blocked = /house|Speed|GTmetrix|moto g power/i.test(userAgent)
        if (blocked || !appConfig.digioh || !appConfig.digioh.scriptUrl) return

        // dynamically load the external script
        const script = document.createElement('script')
        script.src = `${window.location.protocol === 'https:' ? 'https://' : 'http://'}${
            appConfig.digioh.scriptUrl
        }?mb=${new Date().getTime()}`
        script.async = true
        document.body.appendChild(script)

        return () => {
            // cleanup if component unmounts
            document.body.removeChild(script)
        }
    }, [])

    const handleOnMenuClick = () => {
        // menuRef.current.classList.add('menu-toggle')
        setMenuToggle(!menuToggle)

        onMenuClick() // handle to show drawer menu
    }

    const handleIconsMouseLeave = () => {
        // don't close the menu if users enter the popover content
        setTimeout(() => {
            if (!hasEnterPopoverContent.current) onAccountMenuClose()
        }, 100)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Tab' && event.shiftKey && isAccountMenuOpen) {
            // Prevent default behavior to keep focus on the popup trigger
            event.preventDefault()
            popoverTriggerRef.current.focus()
        }
    }

    return (
        <Box
            {...styles.container}
            {...props}
            id="sticky-header"
            backgroundImage={[
                `url(${getAssetUrl('static/img/header/Textured_Header_Mobile.jpg')})`,
                `url(${getAssetUrl('static/img/header/Textured_Header_Mobile.jpg')})`,
                `url(${getAssetUrl('static/img/header/Textured_Header_Desktop.jpg')})`,
                `url(${getAssetUrl('static/img/header/Textured_Header_Desktop.jpg')})`
            ]}
        >
            <Box {...styles.content}>
                {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}

                {/* header */}
                <Box display={{base: 'block', sm: 'block', md: 'block', lg: 'block'}}>
                    <Flex
                        alignItems={['center', 'center', 'center', 'center']}
                        justifyContent={[
                            'space-between',
                            'space-between',
                            'space-between',
                            'space-between'
                        ]}
                        // marginBottom={[1, 1, 2, 2]}
                        paddingLeft={{base: pxToRem(15), sm: pxToRem(15), md: '0', lg: '0'}}
                        paddingRight={{base: pxToRem(15), sm: pxToRem(15), md: '0', lg: '0'}}
                        paddingTop={[3, 3, 0, 0]}
                        marginBottom={{base: pxToRem(19), sm: pxToRem(8), md: '0', lg: '0'}}
                    >
                        {/* menu icon - should be displayed on mobile and tablet */}
                        <Flex
                            width={'25%'}
                            display={{base: 'flex', sm: 'flex', md: 'none', lg: 'none'}}
                            // display={{ base: 'block', sm: 'block', md: 'none', lg: 'none' }}
                        >
                            {/* menu icon */}
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                title={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu.open_dialog',
                                    defaultMessage: 'Opens a dialog'
                                })}
                                ref={menuRef}
                                sx={menuToggle ? toggleMenuIconStyle : menuIconStyle}
                                icon={<i className="menu-icon fa fa-bars"></i>}
                                variant="unstyled"
                                display={{md: 'none', lg: 'none'}}
                                {...styles.icons}
                                onClick={handleOnMenuClick}
                            />
                        </Flex>

                        {/* header logo */}
                        <Box
                            display={{base: 'flex', sm: 'flex', md: 'block', lg: 'block'}}
                            width={'50%'}
                            justifyContent={'center'}
                        >
                            <IconButton
                                as={'a'}
                                href="/"
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo'
                                })}
                                sx={headerLogoStyle}
                                icon={
                                    <picture>
                                        <source
                                            srcSet={getAssetUrl(
                                                'static/img/header/header-logo-desk.png'
                                            )}
                                            media={`(min-width: ${sizes.container.md})`}
                                        />
                                        <source
                                            srcSet={getAssetUrl(
                                                'static/img/header/header-logo-mob.png'
                                            )}
                                            media={`(min-width: ${sizes.container.sm})`}
                                        />
                                        <img
                                            src={getAssetUrl(
                                                'static/img/header/header-logo-mob.png'
                                            )}
                                            height="66"
                                            width="250"
                                            alt="Cavender's"
                                        />
                                    </picture>
                                }
                                {...styles.logoIcon}
                                variant="unstyled"
                                onClick={onLogoClick}
                            />
                        </Box>

                        {/* inner right bar */}
                        <Flex
                            width={['25%', '25%', '50%', '50%']}
                            columnGap={[2, 2, 12, 12]}
                            alignItems={['center', 'center', 'center', 'center']}
                            justifyContent={['flex-end', 'flex-end', 'flex-end', 'flex-end']}
                        >
                            {/* search */}
                            <HideSeachOnMobile>
                                <SearchBar />
                            </HideSeachOnMobile>

                            {/* store locator icon */}
                            {STORE_LOCATOR_IS_ENABLED && (
                                <IconButton
                                    as={RouteLink}
                                    to="/storelocator"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Store Locator',
                                        id: 'header.button.assistive_msg.store_locator'
                                    })}
                                    icon={<StoreLocatorIcon {...styles.storeLocatorIcon} />}
                                    {...styles.icons}
                                    variant="unstyled"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    verticalAlign="middle"
                                    textDecoration="none"
                                />
                            )}

                            <Popover
                                isLazy
                                arrowSize={15}
                                isOpen={
                                    miniCartItems?.basketItems?.length > 0 &&
                                    isAccountMenuOpen &&
                                    !isMobile
                                }
                                placement="bottom-end"
                                onClose={onAccountMenuClose}
                                onOpen={onAccountMenuOpen}
                            >
                                <PopoverTrigger>
                                    <IconButton
                                        as={'a'}
                                        href="/cart"
                                        title={
                                            isMobile &&
                                            intl.formatMessage({
                                                id: 'header.button.view_cart',
                                                defaultMessage: 'View Cart'
                                            })
                                        }
                                        aria-label={intl.formatMessage({
                                            id: 'header.button.assistive_msg.my_account_menu',
                                            defaultMessage: 'Open account menu'
                                        })}
                                        icon={
                                            <IconButton
                                                as={'a'}
                                                aria-label={intl.formatMessage(
                                                    {
                                                        id: 'header.button.assistive_msg.my_cart_with_num_items',
                                                        defaultMessage:
                                                            'My cart, number of items: {numItems}'
                                                    },
                                                    {numItems: totalItemsWithGiftCards}
                                                )}
                                                icon={
                                                    <>
                                                        <img
                                                            src={getAssetUrl(
                                                                'static/img/header/cart.svg'
                                                            )}
                                                            height="25"
                                                            width="25"
                                                            alt={intl.formatMessage({
                                                                id: 'checkout_header.mini_cart.cart',
                                                                defaultMessage: 'Cart'
                                                            })}
                                                        />
                                                        {basket && totalItemsWithGiftCards > 0 && (
                                                            <Badge
                                                                variant="notification"
                                                                sx={{
                                                                    ...cartBadgeStyle,
                                                                    backgroundColor: isMobile
                                                                        ? 'cav_hover_brightred'
                                                                        : isAboveHeaderVisible
                                                                        ? 'cav_hover_brightred'
                                                                        : 'cav_btn_light_black',
                                                                    borderColor:
                                                                        isAboveHeaderVisible
                                                                            ? 'cav_hover_brightred'
                                                                            : 'cav_btn_light_black',
                                                                    _hover: {
                                                                        backgroundColor:
                                                                            isAboveHeaderVisible
                                                                                ? 'cav_dark_red'
                                                                                : 'cav_hover_brightred',
                                                                        borderColor:
                                                                            isAboveHeaderVisible
                                                                                ? 'cav_dark_red'
                                                                                : 'cav_hover_brightred'
                                                                    }
                                                                }}
                                                            >
                                                                {totalItemsWithGiftCards}
                                                            </Badge>
                                                        )}
                                                    </>
                                                }
                                                variant="unstyled"
                                                marginRight={[0, 0, 4, 4]}
                                                marginLeft={[2, 2, 0, 0]}
                                                {...styles.icons}
                                                // onClick={onMyCartClick}
                                                display="inline-flex"
                                                alignItems="center"
                                                justifyContent="flex-start"
                                                verticalAlign="middle"
                                                textDecoration="none"
                                            />
                                        }
                                        variant="unstyled"
                                        {...styles.icons}
                                        {...styles.arrowDown}
                                        {...getAccountMenuButtonProps()}
                                        onMouseOver={onAccountMenuOpen}
                                        onMouseLeave={handleIconsMouseLeave}
                                        onPointerEnter={onAccountMenuOpen}
                                        onPointerLeave={handleIconsMouseLeave}
                                        ref={popoverTriggerRef}
                                        onKeyDown={handleKeyDown}
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        verticalAlign="middle"
                                        textDecoration="none"
                                    />
                                </PopoverTrigger>

                                <PopoverContent
                                    {...styles.popoverContent}
                                    onMouseLeave={() => {
                                        hasEnterPopoverContent.current = false
                                        onAccountMenuClose()
                                    }}
                                    onMouseOver={() => {
                                        hasEnterPopoverContent.current = true
                                    }}
                                    {...getAccountMenuDisclosureProps()}
                                    sx={{
                                        width: pxToRem(334),
                                        borderRadius: '0',
                                        marginRight: pxToRem(30)
                                    }}
                                >
                                    <PopoverArrow />
                                    <PopoverHeader
                                        sx={{
                                            padding: '0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            ...cartHeadingStyle
                                        }}
                                    >
                                        <Box as="h5">
                                            {intl.formatMessage({
                                                defaultMessage: 'ITEMS IN YOUR CART'
                                            })}{' '}
                                            ({totalItemsWithGiftCards})
                                        </Box>
                                        <Box sx={{cursor: 'pointer'}} onClick={onAccountMenuClose}>
                                            X
                                        </Box>
                                    </PopoverHeader>
                                    <PopoverBody sx={cartBodyStyle}>
                                        <Box as="nav">
                                            <Box data-testid="account-detail-nav">
                                                {miniCartItems?.basketItems?.map((item) => {
                                                    const {
                                                        id,
                                                        name,
                                                        price,
                                                        priceAfterItemDiscount,
                                                        quantity,
                                                        imageUrl,
                                                        priceData,
                                                        tieredPrices,
                                                        productUrl,
                                                        variationValues,
                                                        isGiftCertificate,
                                                        recipientName,
                                                        recipientEmail,
                                                        senderName,
                                                        message,
                                                        currency,
                                                        isPhysicalGiftCard
                                                    } = item
                                                    if (isGiftCertificate) {
                                                        return (
                                                            <Flex key={id} gap={2}>
                                                                <Box flex="0 0 29%">
                                                                    <Link href={productUrl}>
                                                                        <img
                                                                            src={imageUrl}
                                                                            alt={name}
                                                                        />
                                                                    </Link>
                                                                </Box>
                                                                <Box
                                                                    flex="1"
                                                                    sx={priceContentStyle}
                                                                >
                                                                    <Text
                                                                        className="property-container"
                                                                        sx={{
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            gap: 1
                                                                        }}
                                                                    >
                                                                        <Text
                                                                            as="span"
                                                                            className="property-item"
                                                                            sx={giftName}
                                                                        >
                                                                            {name}
                                                                        </Text>
                                                                        <span className="property-item">
                                                                            Emailed to:{' '}
                                                                            {recipientName}
                                                                        </span>
                                                                        {recipientEmail && (
                                                                            <span className="property-item fullWidth">
                                                                                {recipientEmail}
                                                                            </span>
                                                                        )}
                                                                        {senderName && (
                                                                            <span className="property-item fullWidth">
                                                                                From: {senderName}
                                                                            </span>
                                                                        )}
                                                                        <Text
                                                                            as="span"
                                                                            sx={{
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            Price:{' '}
                                                                            <FormattedNumber
                                                                                value={price}
                                                                                style="currency"
                                                                                currency={
                                                                                    basket?.currency
                                                                                }
                                                                            />
                                                                        </Text>
                                                                        <Text as="span">
                                                                            Delivery Method: Email
                                                                            Delivery
                                                                        </Text>
                                                                    </Text>
                                                                </Box>
                                                            </Flex>
                                                        )
                                                    } else {
                                                        const productAttributes = {}
                                                        const needChange =
                                                            pdpVariationCategory?.includes(
                                                                item?.primaryCategoryId ||
                                                                    item?.c_primaryCatergoryID
                                                            )
                                                        if (variationValues?.waist)
                                                            productAttributes['Waist'] =
                                                                variationValues?.waist

                                                        if (variationValues?.size) {
                                                            if (needChange) {
                                                                productAttributes['Waist'] =
                                                                    variationValues?.size
                                                            } else {
                                                                productAttributes['Size'] =
                                                                    variationValues?.size
                                                            }
                                                        }

                                                        if (variationValues?.width) {
                                                            if (needChange) {
                                                                productAttributes['Length'] =
                                                                    variationValues?.width
                                                            } else {
                                                                productAttributes['Width'] =
                                                                    variationValues?.width
                                                            }
                                                        }

                                                        if (variationValues?.length)
                                                            productAttributes['Length'] =
                                                                variationValues?.length
                                                        if (quantity)
                                                            productAttributes['Qty'] = quantity

                                                        const hasWidth = variationValues?.width

                                                        return (
                                                            <Flex key={id} gap={2}>
                                                                <Box flex="0 0 29%">
                                                                    <Link href={productUrl}>
                                                                        <img
                                                                            src={imageUrl}
                                                                            alt={name}
                                                                        />
                                                                    </Link>
                                                                </Box>
                                                                <Box
                                                                    flex="1"
                                                                    sx={priceContentStyle}
                                                                >
                                                                    <Link href={productUrl}>
                                                                        {name}
                                                                    </Link>
                                                                    <Text className="property-container">
                                                                        {Object.entries(
                                                                            productAttributes
                                                                        ).map(
                                                                            (
                                                                                [key, value],
                                                                                index
                                                                            ) => (
                                                                                <span
                                                                                    key={index}
                                                                                    className={
                                                                                        hasWidth &&
                                                                                        key ===
                                                                                            'Qty'
                                                                                            ? 'qty-new-line'
                                                                                            : ''
                                                                                    }
                                                                                >
                                                                                    {index % 2 ===
                                                                                        1 &&
                                                                                        !(
                                                                                            hasWidth &&
                                                                                            key ===
                                                                                                'Qty'
                                                                                        ) && (
                                                                                            <span className="pipe">
                                                                                                |
                                                                                            </span>
                                                                                        )}
                                                                                    {key ===
                                                                                        'Size' &&
                                                                                    isPhysicalGiftCard ? (
                                                                                        <>
                                                                                            {key}:{' '}
                                                                                            <FormattedNumber
                                                                                                value={
                                                                                                    value
                                                                                                }
                                                                                                style="currency"
                                                                                                currency={
                                                                                                    currency
                                                                                                }
                                                                                            />
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            {key}:{' '}
                                                                                            {key ===
                                                                                                'Size' ||
                                                                                            key ===
                                                                                                'Waist'
                                                                                                ? item?.c_selectedVariationSize ||
                                                                                                  value
                                                                                                : value}
                                                                                        </>
                                                                                    )}
                                                                                </span>
                                                                            )
                                                                        )}
                                                                    </Text>
                                                                    {priceData?.currentPrice !=
                                                                        priceData?.listPrice && (
                                                                        <Text
                                                                            sx={
                                                                                priceDiscountContentStyle
                                                                            }
                                                                        >
                                                                            <FormattedNumber
                                                                                value={
                                                                                    priceData?.listPrice
                                                                                }
                                                                                style="currency"
                                                                                currency={
                                                                                    basket?.currency
                                                                                }
                                                                            />
                                                                        </Text>
                                                                    )}
                                                                    <Text fontWeight="bold">
                                                                        <FormattedNumber
                                                                            value={
                                                                                priceData?.currentPrice
                                                                            }
                                                                            style="currency"
                                                                            currency={
                                                                                basket?.currency
                                                                            }
                                                                        />
                                                                    </Text>
                                                                </Box>
                                                            </Flex>
                                                        )
                                                    }
                                                })}
                                            </Box>
                                        </Box>
                                    </PopoverBody>
                                    <PopoverFooter sx={cartFooterStyles}>
                                        <Box className="mini-cart-subtotals">
                                            <Text>
                                                {intl.formatMessage({
                                                    id: 'checkout_header.mini_cart.sub_total',
                                                    defaultMessage: 'Order Subtotal'
                                                })}
                                                :{' '}
                                                <FormattedNumber
                                                    value={miniCartItems?.productSubTotal}
                                                    style="currency"
                                                    currency={basket?.currency}
                                                />
                                            </Text>
                                            <Box justifyContent="center" className="btn-container">
                                                <a className="button" href="/cart">
                                                    {intl.formatMessage({
                                                        id: 'checkout_header.mini_cart.view_cart',
                                                        defaultMessage: 'View Cart'
                                                    })}
                                                </a>
                                            </Box>
                                        </Box>
                                    </PopoverFooter>
                                </PopoverContent>
                            </Popover>
                        </Flex>
                    </Flex>
                </Box>
                {/* header end here */}

                {/* nav menu */}
                <Box {...styles.bodyContainer}>{children}</Box>

                {/* search for mobile */}
                <HideSeachOnDesktop>
                    <SearchBar />
                </HideSeachOnDesktop>
                {/* </Flex> */}
            </Box>
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ]),
    menuToggle: PropTypes.oneOf(true, false),
    setMenuToggle: PropTypes.func
}

export default Header
